import { useMemo } from 'react';
import { useTheme, Box, alpha } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RiskBadge from './RiskBadge';
import FlagChips from './FlagChips';

const pct = (v) => (v != null ? `${(v * 100).toFixed(0)}%` : '-');
const num = (v) => (v != null ? v.toFixed(3) : '-');

const AnomalyEntityGrid = ({ rows = [], loading = false, type = 'user' }) => {
  const theme = useTheme();

  const columns = useMemo(() => {
    const idCol = type === 'user'
      ? { field: 'userId',   headerName: 'User',  width: 210,
          renderCell: ({ value }) => <Box sx={{ color: 'primary.main' }}>{value}</Box> }
      : { field: 'crewCode', headerName: 'Crew',  width: 80,
          renderCell: ({ value }) => <Box sx={{ color: 'secondary.main' }}>{value}</Box> };

    const diversityCol = type === 'user'
      ? { field: 'uniqueCrewCount', headerName: 'Crews', width: 80, type: 'number' }
      : { field: 'uniqueUserCount', headerName: 'Users', width: 100, type: 'number' };

    return [
      {
        field: 'risk',
        headerName: 'Risk',
        width: 80,
        renderCell: ({ value }) => <RiskBadge risk={value} />,
      },
      idCol,
      { 
        field: 'totalChanges', 
        headerName: 'Changes', 
        width: 70, 
        type: 'number' 
      },
      diversityCol,
      { 
        field: 'offHoursRatio',    
        headerName: 'Off-hours',   
        align: 'right',
        width: 75,  
        renderCell: ({ value }) => pct(value) 
      },
      { 
        field: 'retroactiveRatio', 
        headerName: 'Retroactive', 
        align: 'right',
        width: 80,  
        renderCell: ({ value }) => pct(value) 
      },
      { 
        field: 'lastMinuteRatio',  
        headerName: '% Last-min',    
        align: 'right',
        width: 80,  
        renderCell: ({ value }) => pct(value) 
      },
      { 
        field: 'weekendRatio',     
        headerName: 'Weekend',     
        align: 'right',
        width: 75,  
        renderCell: ({ value }) => pct(value) 
      },
      ...(type === 'user' ? [
        { 
          field: 'pairConcentration', 
          headerName: 'Conc. (HHI)', 
          align: 'right',
          width: 85, 
          renderCell: ({ value }) => num(value) 
        },
        { 
          field: 'burstScore',        
          headerName: 'Burst',        
          align: 'right',
          width: 75,  
          renderCell: ({ value }) => num(value) 
        },
      ] : []),
      {
        field: 'scores',
        headerName: 'IF Score',
        align: 'right',
        width: 75,
        renderCell: ({ value }) => (
          <Box>
            {num(value?.isolationForest)}
          </Box>
        ),
      },
      {
        field: 'flags',
        headerName: 'Flags',
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ value }) => {
          return (
            <Box
              sx={{
                height: '100%',
                alignItems: 'flex-start',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <FlagChips flags={value} max={2} />
            </Box>
          )
        }
      },
    ];
  }, [type]);

  const RISK_BG = {
    CRITICAL: alpha(theme.palette.error.main, 0.12),
    HIGH:     alpha('#f57c00', 0.10),
    MEDIUM:   alpha('#f9a825', 0.08),
    LOW:      'transparent',
  };

  return (
    <DataGrid
      rows={rows.map((r, i) => ({ id: i, ...r }))}
      columns={columns}
      loading={loading}
      density="compact"
      disableRowSelectionOnClick
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      pageSizeOptions={[25, 50, 100]}
      getRowClassName={({ row }) => `risk-${row.risk}`}
      sx={{
        border: 'none',
        '& .risk-CRITICAL': { bgcolor: RISK_BG.CRITICAL },
        '& .risk-HIGH':     { bgcolor: RISK_BG.HIGH },
        '& .risk-MEDIUM':   { bgcolor: RISK_BG.MEDIUM },
        '& .MuiDataGrid-columnHeaders': {
          bgcolor: theme.palette.background.alt ?? theme.palette.background.paper,
        },
      }}
    />
  );
};

export default AnomalyEntityGrid;
